import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/helpers";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Capture GPS location (best-effort, always resolves; never blocks login for long).
const getGeo = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ location_permission: "unavailable" });
    let done = false;
    const finish = (v) => { if (!done) { done = true; resolve(v); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => finish({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        location_permission: "granted",
      }),
      (err) => finish({ location_permission: err.code === 1 ? "denied" : "unavailable" }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    setTimeout(() => finish({ location_permission: "timeout" }), 9000);
  });

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    const run = async () => {
      if (!sessionId) { navigate("/login"); return; }
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        if (res.data?.session_token) {
          try { localStorage.setItem("vns_session_token", res.data.session_token); } catch (e) { /* ignore */ }
        }
        const me = await api.get("/auth/me");
        setUser(me.data);
        // Best-effort security logging: capture device + GPS location for this login.
        getGeo().then((geo) => { api.post("/security/login-activity", geo).catch(() => {}); });
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const denied = e?.response?.status === 403;
        navigate(denied ? "/login?denied=1" : "/login", { replace: true });
      }
    };
    run();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gold">
      <img src={LOGO} alt="VNS" className="w-24 h-24 object-contain mb-6 animate-pulse bg-white rounded-xl p-2" />
      <p className="text-navy-900 font-heading text-lg">Signing you in…</p>
    </div>
  );
}
